import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BottomSheet, StatusActionButton, ExecutorSelector, Timeline, TimelineSegment, TimelineHandle } from '../../../shared/ui';
import { sessionsV2Api, CreateSessionV2Request, CreateSessionActivityRequest } from '../../../api/sessionsV2Api';
import { compressImageFiles } from '../../../utils/imageCompression';
import { openFileInput } from '../../../utils/filePicker';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import { contractsV2Api, ContractV2 } from '../../../api/contractsV2Api';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useToast } from '../../../shared/contexts/ToastContext';
import HorizontalCategoryTabs from '../../../shared/ui/HorizontalCategoryTabs';
import '../../../shared/ui/forms/WorkTypeSelector.css';
import './CreateSessionV2Modal.css';

interface CreateSessionV2ModalProps {
  isOpen: boolean;
  siteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface ActivityItem {
  id: number | string;
  typeOfWorkId: number;
  typeOfWorkName?: string;
  /** Контракт (сделка). Если задан — активность привязана к контракту, а не к виду работ. */
  contractId?: number;
  contractTitle?: string;
  timeStart: Date;
  rate: number;
  hours: number;
  percentage: number;
  amount: number;
  isNew: boolean;
  isModified: boolean;
  comment?: string;
  /** Прикреплённые фото к комментарию (локально до создания смены). */
  activityCommentPhotos?: File[];
  /** Причина допа (отдельно от комментария активности). */
  additionalWorkReason?: string;
  isAdditionalWork?: boolean;
  /** Прикреплённые фото к доп.работе (локально до создания смены). */
  additionalWorkPhotos?: File[];
}

// Мемоизированный компонент для элемента активности
const ActivityItemComponent = React.memo<{
  item: ActivityItem;
  index: number;
  activities: ActivityItem[];
  sessionEnd: Date;
  formatTime: (date: Date) => string;
  formatDuration: (hours: number) => { hours: number; minutes: number };
  getActivityEndTime: (items: ActivityItem[], index: number, sessionEndTime: Date) => Date;
  updateActivity: (id: number | string, updates: Partial<ActivityItem>) => void;
  handleDeleteActivity: (id: number | string) => void;
  onCommentClick: (item: ActivityItem) => void;
  onAdditionalWorkClick: (item: ActivityItem) => void;
  onActivityTypeClick: (item: ActivityItem) => void;
  onActivityTypeGlobalPick: (item: ActivityItem) => void;
  onCloseInlinePicker: () => void;
  onInlineQuickPick: (item: ActivityItem, typeId: number) => void;
  quickWorkTypeTabs: TypeOfWorkV2[];
  inlinePickerActivityId: number | string | null;
  isLast: boolean;
  /** Упрощённый режим: редактируемая продолжительность, без времени */
  isSimpleMode?: boolean;
}>(({
  item,
  index,
  activities,
  sessionEnd,
  formatTime,
  formatDuration,
  getActivityEndTime,
  updateActivity,
  handleDeleteActivity,
  onCommentClick,
  onAdditionalWorkClick,
  onActivityTypeClick,
  onActivityTypeGlobalPick,
  onCloseInlinePicker,
  onInlineQuickPick,
  quickWorkTypeTabs,
  inlinePickerActivityId,
  isLast,
  isSimpleMode = false
}) => {
  const endTime = getActivityEndTime(activities, index, sessionEnd);
  const nextActivity = index < activities.length - 1 ? activities[index + 1] : null;
  const isInlinePickerOpen = inlinePickerActivityId === item.id;

  return (
    <React.Fragment>
      <div className="activity-item">
        <div className="activity-row activity-row-1">
          <div className="activity-col-time-block">
            <div className="activity-time-block">
              <div className="activity-time-block-duration">
                <span>{formatDuration(item.hours).hours} ч</span>
                {formatDuration(item.hours).minutes > 0 && (
                  <span> {formatDuration(item.hours).minutes} м</span>
                )}
              </div>
              <div className="activity-time-block-percent">
                <span className="percent-value">{item.percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
          <div className="activity-vertical-separator"></div>
          <div className="activity-col-type-rate-amount">
            {isInlinePickerOpen ? (
              <div className="activity-inline-picker">
                <button
                  type="button"
                  className="activity-inline-picker-btn activity-inline-picker-btn-back"
                  onClick={onCloseInlinePicker}
                  title="Закрыть быстрый выбор"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  type="button"
                  className="activity-inline-picker-btn activity-inline-picker-btn-global"
                  onClick={() => onActivityTypeGlobalPick(item)}
                  title="Открыть полный выбор активностей"
                >
                  <i className="ri-list-check-2"></i>
                </button>
                {quickWorkTypeTabs.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className="activity-inline-picker-btn"
                    onClick={() => onInlineQuickPick(item, type.id)}
                    title={`Выбрать: ${type.name}`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            ) : item.contractId ? (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы изменить контракт или переключить на вид работ"
              >
                <span className="activity-contract-badge">Сделка</span>
                <span className="activity-contract-title">{item.contractTitle || `Контракт #${item.contractId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            ) : (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable activity-contract-display--worktype-only"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы выбрать активность из быстрых кнопок"
              >
                <span className="activity-contract-title">{item.typeOfWorkName || `Вид работ #${item.typeOfWorkId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            )}
            <div className="activity-col-rate-amount">
              {item.contractId ? (
                <div className="activity-col-amount">
                  <label>Сумма</label>
                  <span className="amount-value" style={{ fontStyle: 'italic' }}>По контракту</span>
                </div>
              ) : (
                <>
                  <div className="activity-col-rate">
                    <label>Ставка</label>
                    <input
                      type="number"
                      value={item.rate || ''}
                      onChange={(e) => updateActivity(item.id, { rate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>
                  <div className="activity-col-amount">
                    <label>Сумма</label>
                    <span className="amount-value">
                      {item.rate > 0 ? `${item.amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽` : '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="activity-col-actions" style={{ flexDirection: 'column', gap: '8px' }}>
            <button
              type="button"
              className="btn-comment"
              onClick={() => onCommentClick(item)}
              title={item.comment ? 'Редактировать комментарий' : 'Добавить комментарий'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                padding: 0,
                background: item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent',
                color: item.comment ? '#4a9eff' : '#888',
                border: item.comment ? '1.5px solid rgba(74, 158, 255, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!item.comment) {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.1)';
                  e.currentTarget.style.color = '#4a9eff';
                  e.currentTarget.style.borderColor = 'rgba(74, 158, 255, 0.4)';
                } else {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.25)';
                  e.currentTarget.style.borderColor = '#4a9eff';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent';
                e.currentTarget.style.color = item.comment ? '#4a9eff' : '#888';
                e.currentTarget.style.borderColor = item.comment ? 'rgba(74, 158, 255, 0.3)' : 'rgba(136, 136, 136, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className={item.comment ? "ri-chat-3-fill" : "ri-chat-3-line"}></i>
            </button>
            <button
              type="button"
              className="btn-additional-work"
              onClick={() => onAdditionalWorkClick(item)}
              title={item.isAdditionalWork ? 'Доп.работа (нажмите чтобы снять)' : 'Пометить как доп.работу'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                padding: 0,
                flexShrink: 0,
                background: item.isAdditionalWork ? 'rgba(255, 170, 0, 0.15)' : 'transparent',
                color: item.isAdditionalWork ? '#ffaa00' : '#888',
                border: item.isAdditionalWork ? '1.5px solid rgba(255, 170, 0, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!item.isAdditionalWork) {
                  e.currentTarget.style.background = 'rgba(255, 170, 0, 0.1)';
                  e.currentTarget.style.color = '#ffaa00';
                  e.currentTarget.style.borderColor = 'rgba(255, 170, 0, 0.4)';
                } else {
                  e.currentTarget.style.background = 'rgba(255, 170, 0, 0.25)';
                  e.currentTarget.style.borderColor = '#ffaa00';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.isAdditionalWork ? 'rgba(255, 170, 0, 0.15)' : 'transparent';
                e.currentTarget.style.color = item.isAdditionalWork ? '#ffaa00' : '#888';
                e.currentTarget.style.borderColor = item.isAdditionalWork ? 'rgba(255, 170, 0, 0.3)' : 'rgba(136, 136, 136, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className={item.isAdditionalWork ? "ri-star-fill" : "ri-star-line"}></i>
            </button>
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleDeleteActivity(item.id)}
              title="Удалить"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </div>
      {/* Разделитель и время между активностями */}
      {isLast ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          {!isSimpleMode && (
            <div className="activity-separator-time">
              <span title="Конец смены" className="activity-start-time-label">
                {formatTime(endTime)}
              </span>
            </div>
          )}
        </div>
      ) : nextActivity ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          {!isSimpleMode && (
            <div className="activity-separator-time">
              <input
                type="time"
                value={`${String(nextActivity.timeStart.getHours()).padStart(2, '0')}:${String(nextActivity.timeStart.getMinutes()).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':');
                  const newTime = new Date(nextActivity.timeStart);
                  newTime.setHours(parseInt(h), parseInt(m));
                  updateActivity(nextActivity.id, { timeStart: newTime });
                }}
                className="activity-time-picker-input"
                id={`time-input-${nextActivity.id}`}
              />
            </div>
          )}
        </div>
      ) : null}
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  if (prevProps.item.id !== nextProps.item.id) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.isSimpleMode !== nextProps.isSimpleMode) return false;
  if (prevProps.inlinePickerActivityId !== nextProps.inlinePickerActivityId) return false;

  const prevHash = `${prevProps.item.timeStart.getTime()}-${prevProps.item.typeOfWorkId}-${prevProps.item.contractId ?? ''}-${prevProps.item.rate}-${prevProps.item.hours}-${prevProps.item.isAdditionalWork}-${prevProps.item.comment ?? ''}-${prevProps.item.additionalWorkReason ?? ''}-${prevProps.item.typeOfWorkName ?? ''}`;
  const nextHash = `${nextProps.item.timeStart.getTime()}-${nextProps.item.typeOfWorkId}-${nextProps.item.contractId ?? ''}-${nextProps.item.rate}-${nextProps.item.hours}-${nextProps.item.isAdditionalWork}-${nextProps.item.comment ?? ''}-${nextProps.item.additionalWorkReason ?? ''}-${nextProps.item.typeOfWorkName ?? ''}`;

  return prevHash === nextHash && prevProps.activities.length === nextProps.activities.length;
});

ActivityItemComponent.displayName = 'ActivityItemComponent';

const CreateSessionV2Modal: React.FC<CreateSessionV2ModalProps> = ({
  isOpen,
  siteId,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [selectedTypeOfWorksId, setSelectedTypeOfWorksId] = useState<number | null>(null);
  const [selectedExecutorId, setSelectedExecutorId] = useState<number | null>(null);
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Состояния для активностей
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [sessionStart, setSessionStart] = useState<Date>(new Date());
  const [sessionEnd, setSessionEnd] = useState<Date>(new Date());
  
  // Состояния для модального окна выбора активностей (Ставка | Сделка)
  const [showWorkTypeSelector, setShowWorkTypeSelector] = useState(false);
  const [activitySourceTab, setActivitySourceTab] = useState<'ставка' | 'сделка'>('ставка');
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<number[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [contracts, setContracts] = useState<ContractV2[]>([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [workTypeSearchQuery, setWorkTypeSearchQuery] = useState('');
  const [selectedWorkTypeCategory, setSelectedWorkTypeCategory] = useState<string | null>(null);
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  /** Редактирование типа активности: id активности или null при добавлении новых */
  const [editingActivityId, setEditingActivityId] = useState<number | string | null>(null);
  const [recentTypeOfWorkIds, setRecentTypeOfWorkIds] = useState<number[]>([]);
  const [loadingRecentActivities, setLoadingRecentActivities] = useState(false);
  const [inlinePickerActivityId, setInlinePickerActivityId] = useState<number | string | null>(null);
  /** Верхняя полоса быстрых кнопок скрыта до нажатия «Добавить активность». */
  const [showQuickAddActivityBar, setShowQuickAddActivityBar] = useState(false);

  // Состояния для временной шкалы
  const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(null);
  const [timelineRef, setTimelineRef] = useState<HTMLDivElement | null>(null);
  const [tempTimelinePositions, setTempTimelinePositions] = useState<Map<number, Date> | null>(null);
  
  /** true = точное время (timeStart/timeEnd), false = упрощённый режим (только продолжительность) */
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  
  // Состояния для предложений времени
  const [timeSuggestions, setTimeSuggestions] = useState<{ primaryStartTime: string; alternativeStartTimes: string[]; primaryEndTime: string; alternativeEndTimes: string[] } | null>(null);

  /** Исполнители объекта — для ставки по умолчанию при добавлении активностей (Ставка) */
  const [siteExecutorsForRates, setSiteExecutorsForRates] = useState<SiteExecutor[]>([]);
  
  // Состояния для комментариев к активностям
  const [showCommentSheet, setShowCommentSheet] = useState(false);
  const [currentActivityForComment, setCurrentActivityForComment] = useState<ActivityItem | null>(null);
  const [activityComment, setActivityComment] = useState<string>('');
  const [commentPhotos, setCommentPhotos] = useState<File[]>([]);
  const [commentPhotoPreviews, setCommentPhotoPreviews] = useState<string[]>([]);
  const commentPhotoUrlsRef = useRef<Set<string>>(new Set());
  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const workTypeSearchInputRef = useRef<HTMLInputElement>(null);
  const contractSearchInputRef = useRef<HTMLInputElement>(null);
  const activityCommentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const additionalWorkReasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Состояния для доп.работы
  const [showAdditionalWorkSheet, setShowAdditionalWorkSheet] = useState(false);
  const [currentActivityForAdditionalWork, setCurrentActivityForAdditionalWork] = useState<ActivityItem | null>(null);
  const [additionalWorkReason, setAdditionalWorkReason] = useState<string>('');
  const [additionalWorkPhotos, setAdditionalWorkPhotos] = useState<File[]>([]);
  const [additionalWorkPhotoPreviews, setAdditionalWorkPhotoPreviews] = useState<string[]>([]);
  const additionalWorkPhotoUrlsRef = useRef<Set<string>>(new Set());
  const additionalWorkFileInputRef = useRef<HTMLInputElement>(null);

  // Ref для предыдущего времени начала смены (для сдвига активностей при изменении начала)
  const prevSessionStartRef = useRef<Date | null>(null);

  useEffect(() => {
    if (isOpen) {
      const loadTimeSuggestions = async () => {
        try {
          // Загружаем предложения времени для объекта
          console.log('Загрузка предложений времени для объекта', siteId);
          const suggestions = await sessionsV2Api.getSessionTimeSuggestions(siteId);
          console.log('Получены предложения времени:', suggestions);
          
          // Сохраняем предложения для отображения в UI
          setTimeSuggestions(suggestions);
          
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          const datePart = `${year}-${month}-${day}`;
          
          // Устанавливаем время начала из предложений или текущее время
          if (suggestions.primaryStartTime && suggestions.primaryStartTime.trim() !== '') {
            console.log('Используется предложенное время начала:', suggestions.primaryStartTime);
            const [hours, minutes] = suggestions.primaryStartTime.split(':');
            // Формируем строку в формате datetime-local (YYYY-MM-DDTHH:mm) в локальном времени
            const timeString = `${datePart}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
            console.log('Установлено время начала:', timeString);
            setTimeStart(timeString);
            // Создаем Date объект для sessionStart (в локальном времени)
            const suggestedStart = new Date(today);
            suggestedStart.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
            setSessionStart(suggestedStart);
          } else {
            console.log('Нет предложений для времени начала, используется текущее время');
            // Fallback: текущее время
            const now = new Date();
            const timeString = now.toISOString().slice(0, 16);
            setTimeStart(timeString);
            setSessionStart(now);
          }
          
          // Устанавливаем время окончания из предложений или через 8 часов
          if (suggestions.primaryEndTime && suggestions.primaryEndTime.trim() !== '') {
            console.log('Используется предложенное время окончания:', suggestions.primaryEndTime);
            const [hours, minutes] = suggestions.primaryEndTime.split(':');
            // Формируем строку в формате datetime-local (YYYY-MM-DDTHH:mm) в локальном времени
            const endString = `${datePart}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
            console.log('Установлено время окончания:', endString);
            setTimeEnd(endString);
            // Создаем Date объект для sessionEnd (в локальном времени)
            const suggestedEnd = new Date(today);
            suggestedEnd.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
            setSessionEnd(suggestedEnd);
          } else {
            console.log('Нет предложений для времени окончания, используется текущее время + 8 часов');
            // Fallback: текущее время + 8 часов
            const now = new Date();
            const end = new Date(now.getTime() + 8 * 60 * 60 * 1000);
            const endString = end.toISOString().slice(0, 16);
            setTimeEnd(endString);
            setSessionEnd(end);
          }
        } catch (error) {
          console.error('Ошибка загрузки предложений времени:', error);
          // При ошибке используем текущее время как fallback
          const now = new Date();
          const timeString = now.toISOString().slice(0, 16);
          setTimeStart(timeString);
          setSessionStart(now);
          
          const end = new Date(now.getTime() + 8 * 60 * 60 * 1000);
          const endString = end.toISOString().slice(0, 16);
          setTimeEnd(endString);
          setSessionEnd(end);
        }
      };
      
      loadTimeSuggestions();
      
      // Исполнитель не выбран по умолчанию - нужно выбрать вручную
      setSelectedExecutorId(null);
      
      setActivities([]);
      prevSessionStartRef.current = null;
      setIsAdvancedMode(false);
      setShowQuickAddActivityBar(false);
      setInlinePickerActivityId(null);
      setRecentTypeOfWorkIds([]);
    }
  }, [isOpen, siteId]);

  useEffect(() => {
    if (isOpen) {
      const loadTypesOfWork = async () => {
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
        }
      };
      void loadTypesOfWork();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !selectedExecutorId || siteId <= 0) {
      setRecentTypeOfWorkIds([]);
      return;
    }

    let cancelled = false;
    const loadRecentActivities = async () => {
      setLoadingRecentActivities(true);
      try {
        const sessions = await sessionsV2Api.getSiteSessions(siteId, {
          siteExecutorId: selectedExecutorId,
          limit: 25
        });

        const recentSessions = sessions.slice(0, 20);
        if (recentSessions.length === 0) {
          if (!cancelled) setRecentTypeOfWorkIds([]);
          return;
        }

        const activitiesMap = await sessionsV2Api.getBatchSessionActivities(
          siteId,
          recentSessions.map((s) => s.id)
        );

        const latestByType = new Map<number, number>();
        recentSessions.forEach((s) => {
          const sessionStartTs = new Date(s.timeStart).getTime();
          const sessionActs = activitiesMap.get(s.id) ?? [];
          sessionActs.forEach((activity) => {
            const activityTs = activity.timeStart
              ? new Date(activity.timeStart).getTime()
              : sessionStartTs;
            const prevTs = latestByType.get(activity.typeOfWorkId) ?? 0;
            if (activityTs > prevTs) {
              latestByType.set(activity.typeOfWorkId, activityTs);
            }
          });
        });

        const sortedRecentIds = Array.from(latestByType.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([typeId]) => typeId);

        if (!cancelled) {
          setRecentTypeOfWorkIds(sortedRecentIds);
        }
      } catch (error) {
        console.error('Ошибка загрузки недавних активностей исполнителя:', error);
        if (!cancelled) {
          setRecentTypeOfWorkIds([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRecentActivities(false);
        }
      }
    };

    void loadRecentActivities();
    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId, selectedExecutorId]);

  useEffect(() => {
    if (!isOpen || siteId <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await siteExecutorsApi.getSiteExecutors(siteId);
        if (!cancelled) setSiteExecutorsForRates(list);
      } catch (err) {
        console.error('Ошибка загрузки исполнителей для ставок:', err);
        if (!cancelled) setSiteExecutorsForRates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId]);

  /** Ставка ₽/ч по умолчанию для выбранного исполнителя (вкладка «Ставка», не сделка) */
  const hourlyDefaultRate = useMemo(() => {
    if (!selectedExecutorId) return 0;
    const ex = siteExecutorsForRates.find(e => e.id === selectedExecutorId);
    return ex?.defaultHourlyRate ?? 0;
  }, [siteExecutorsForRates, selectedExecutorId]);

  const quickWorkTypeTabs = useMemo(() => {
    if (typesOfWork.length === 0) return [];
    const recentSet = new Set(recentTypeOfWorkIds);
    const recentTypes = recentTypeOfWorkIds
      .map((id) => typesOfWork.find((t) => t.id === id))
      .filter((t): t is TypeOfWorkV2 => Boolean(t));
    const restTypes = typesOfWork.filter((t) => !recentSet.has(t.id));
    return [...recentTypes, ...restTypes];
  }, [typesOfWork, recentTypeOfWorkIds]);

  // Загружаем виды работ при открытии модального окна выбора (вкладка Ставка)
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'ставка') {
      const loadTypesOfWork = async () => {
        setLoadingWorkTypes(true);
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
          showError('Ошибка загрузки видов работ');
        } finally {
          setLoadingWorkTypes(false);
        }
      };
      loadTypesOfWork();
    }
  }, [showWorkTypeSelector, activitySourceTab, showError]);

  // Загружаем контракты при переключении на вкладку Сделка
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'сделка' && siteId) {
      const loadContracts = async () => {
        setLoadingContracts(true);
        try {
          const data = await contractsV2Api.getSiteContracts(siteId, { onlyActive: true });
          setContracts(data);
        } catch (error) {
          console.error('Ошибка загрузки контрактов:', error);
          showError('Ошибка загрузки контрактов');
        } finally {
          setLoadingContracts(false);
        }
      };
      loadContracts();
    }
  }, [showWorkTypeSelector, activitySourceTab, siteId, showError]);

  // Фокус в поле поиска при открытии выбора активностей / смене вкладки (удобный ввод с клавиатуры)
  useEffect(() => {
    if (!showWorkTypeSelector) return;
    const id = window.setTimeout(() => {
      if (activitySourceTab === 'ставка') {
        workTypeSearchInputRef.current?.focus({ preventScroll: true });
      } else {
        contractSearchInputRef.current?.focus({ preventScroll: true });
      }
    }, 150);
    return () => clearTimeout(id);
  }, [showWorkTypeSelector, activitySourceTab]);

  useEffect(() => {
    if (!showCommentSheet) return;
    const id = window.setTimeout(() => {
      activityCommentTextareaRef.current?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(id);
  }, [showCommentSheet]);

  useEffect(() => {
    if (!showAdditionalWorkSheet) return;
    const id = window.setTimeout(() => {
      additionalWorkReasonTextareaRef.current?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(id);
  }, [showAdditionalWorkSheet, currentActivityForAdditionalWork?.id, currentActivityForAdditionalWork?.isAdditionalWork]);

  // Синхронизируем время сессии с полями формы
  useEffect(() => {
    if (timeStart) {
      const newStart = new Date(timeStart);
      setSessionStart(newStart);
      
      // Если время окончания не установлено, устанавливаем +8 часов
      if (!timeEnd) {
        const newEnd = new Date(newStart.getTime() + 8 * 60 * 60 * 1000);
        setSessionEnd(newEnd);
      }
    }
  }, [timeStart, timeEnd]);

  useEffect(() => {
    if (timeEnd) {
      const newEnd = new Date(timeEnd);
      setSessionEnd(newEnd);
    }
  }, [timeEnd]);

  // ============================================================================
  // ЛОГИКА УПРАВЛЕНИЯ АКТИВНОСТЯМИ
  // ============================================================================

  /**
   * Вычисляет время окончания активности
   */
  const getActivityEndTime = (items: ActivityItem[], index: number, sessionEndTime: Date): Date => {
    if (index < items.length - 1) {
      return items[index + 1].timeStart;
    }
    return sessionEndTime;
  };

  /**
   * Пересчитывает статистику для всех активностей
   */
  const sessionTimeRange = useMemo(() => {
    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    const currentEnd = timeEnd ? new Date(timeEnd) : sessionEnd;
    return { start: currentStart, end: currentEnd };
  }, [timeStart, timeEnd, sessionStart, sessionEnd]);

  const recalculateStats = useCallback((items: ActivityItem[], advancedMode?: boolean): ActivityItem[] => {
    const useAdvanced = advancedMode ?? isAdvancedMode;
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    
    if (!currentStart || !currentEnd) {
      return items;
    }
    
    if (!useAdvanced) {
      // Упрощённый режим: hours — источник правды. Синхронизируем timeStart для timeline.
      const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const totalHours = sorted.reduce((s, i) => s + (i.hours || 0), 0);
      let cumulativeMs = currentStart.getTime();
      const MS_PER_HOUR = 3600000;
      return sorted.map((item) => {
        const h = Math.max(0, item.hours || 0);
        const percentage = totalHours > 0 ? (h / totalHours) * 100 : 0;
        const amount = h * item.rate;
        const start = new Date(cumulativeMs);
        cumulativeMs += h * MS_PER_HOUR;
        return { ...item, hours: h, percentage, amount, timeStart: start };
      });
    }
    
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalSessionDuration = currentEnd.getTime() - currentStart.getTime();
    const MS_IN_HOUR = 3600000;
    
    return sorted.map((item, index) => {
      const endTime = getActivityEndTime(sorted, index, currentEnd);
      const duration = endTime.getTime() - item.timeStart.getTime();
      
      const hours = Math.max(0, duration / MS_IN_HOUR);
      const percentage = totalSessionDuration > 0 ? (duration / totalSessionDuration) * 100 : 0;
      const amount = hours * item.rate;
      
      return {
        ...item,
        hours,
        percentage,
        amount
      };
    });
  }, [sessionTimeRange, isAdvancedMode]);

  // Синхронизируем активности с временем смены (только в продвинутом режиме)
  useEffect(() => {
    if (!isAdvancedMode || activities.length === 0 || !timeStart || !timeEnd) {
      if (timeStart) {
        prevSessionStartRef.current = new Date(timeStart);
      }
      return;
    }
    const newSessionStart = new Date(timeStart);
    const prevStart = prevSessionStartRef.current;
    const startChanged = prevStart != null && prevStart.getTime() !== newSessionStart.getTime();

    if (startChanged) {
      const timeDiff = newSessionStart.getTime() - prevStart.getTime();
      setActivities(prev => {
        const next = prev.map(activity => ({
          ...activity,
          timeStart: new Date(activity.timeStart.getTime() + timeDiff),
          isModified: true
        }));
        return recalculateStats(next);
      });
    } else {
      setActivities(prev => recalculateStats(prev));
    }
    prevSessionStartRef.current = newSessionStart;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно только timeStart/timeEnd
  }, [timeStart, timeEnd, isAdvancedMode]);

  /**
   * Конвертирует часы в точки времени, сохраняя пропорции.
   * При переключении из упрощённого в продвинутый режим.
   */
  const hoursToTimePoints = (
    items: ActivityItem[],
    sessionStart: Date,
    sessionEnd: Date
  ): ActivityItem[] => {
    if (items.length === 0) return items;
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalHours = sorted.reduce((s, i) => s + (i.hours || 0), 0);
    const sessionDurationMs = sessionEnd.getTime() - sessionStart.getTime();
    const MS_PER_HOUR = 3600000;
    let cumulativeMs = 0;
    return sorted.map((item) => {
      const h = item.hours || 0;
      const start = new Date(sessionStart.getTime() + cumulativeMs);
      const proportion = totalHours > 0 ? h / totalHours : 1 / sorted.length;
      cumulativeMs += proportion * sessionDurationMs;
      return { ...item, timeStart: start, hours: h, isModified: true };
    });
  };

  /**
   * Равномерно распределяет активности
   */
  const distributeActivitiesEvenly = (
    items: ActivityItem[], 
    availableStart: Date, 
    availableEnd: Date
  ): ActivityItem[] => {
    if (items.length === 0) return items;
    
    const MIN_DURATION = 60 * 1000;
    const totalAvailable = availableEnd.getTime() - availableStart.getTime();
    const durationPerActivity = Math.max(MIN_DURATION, totalAvailable / items.length);
    
    return items.map((item, index) => {
      const newStartTime = availableStart.getTime() + (durationPerActivity * index);
      return {
        ...item,
        timeStart: new Date(newStartTime),
        isModified: true
      };
    });
  };

  /**
   * Открывает модальное окно для выбора активностей (Ставка | Сделка)
   */
  const handleAddActivity = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setInlinePickerActivityId(null);
    setEditingActivityId(null);
    setActivitySourceTab('ставка');
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  };

  /**
   * Открывает модальное окно для изменения типа активности (вид работ или контракт)
   */
  const handleOpenActivityTypeEditor = useCallback((item: ActivityItem) => {
    setInlinePickerActivityId(null);
    setEditingActivityId(item.id);
    if (item.contractId) {
      setActivitySourceTab('сделка');
      setSelectedContractIds([item.contractId]);
      setSelectedWorkTypeIds([]);
    } else {
      setActivitySourceTab('ставка');
      setSelectedWorkTypeIds(item.typeOfWorkId ? [item.typeOfWorkId] : []);
      setSelectedContractIds([]);
    }
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  }, []);

  const handleToggleActivityInlinePicker = useCallback((item: ActivityItem) => {
    setInlinePickerActivityId((prev) => (prev === item.id ? null : item.id));
  }, []);

  const handleCloseInlinePicker = useCallback(() => {
    setInlinePickerActivityId(null);
  }, []);

  /**
   * Переключает выбор вида работы (ставка). В режиме редактирования — один выбор.
   */
  const toggleWorkTypeSelection = (typeId: number) => {
    setSelectedWorkTypeIds(prev => {
      if (editingActivityId !== null) {
        return prev[0] === typeId ? [] : [typeId];
      }
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      }
      return [...prev, typeId];
    });
  };

  /**
   * Переключает выбор контракта (сделка). В режиме редактирования — один выбор.
   */
  const toggleContractSelection = (contractId: number) => {
    setSelectedContractIds(prev => {
      if (editingActivityId !== null) {
        return prev[0] === contractId ? [] : [contractId];
      }
      if (prev.includes(contractId)) {
        return prev.filter(id => id !== contractId);
      }
      return [...prev, contractId];
    });
  };

  /**
   * Подтверждает выбор видов работ и создает активности
   */
  const handleConfirmWorkTypeSelection = () => {
    const isDealMode = activitySourceTab === 'сделка';
    const itemsCount = isDealMode ? selectedContractIds.length : selectedWorkTypeIds.length;
    
    if (itemsCount === 0) {
      showError(isDealMode ? 'Выберите хотя бы один контракт' : 'Выберите хотя бы один вид работы');
      return;
    }

    // Режим редактирования: обновляем одну активность
    if (editingActivityId !== null) {
      const isEditDeal = isDealMode;
      const contractId = isEditDeal ? selectedContractIds[0] : undefined;
      const typeId = isEditDeal ? undefined : selectedWorkTypeIds[0];
      
      if (isEditDeal && contractId) {
        const contract = contracts.find(c => c.id === contractId);
        if (contract) {
          updateActivity(editingActivityId, {
            typeOfWorkId: contract.typeOfWorkId,
            contractId: contract.id,
            contractTitle: contract.title
          });
        }
      } else if (!isEditDeal && typeId) {
        updateActivity(editingActivityId, {
          typeOfWorkId: typeId,
          contractId: undefined,
          contractTitle: undefined
        });
      }
      setShowWorkTypeSelector(false);
      setEditingActivityId(null);
      setSelectedWorkTypeIds([]);
      setSelectedContractIds([]);
      setWorkTypeSearchQuery('');
      setContractSearchQuery('');
      setSelectedWorkTypeCategory(null);
      showSuccess('Тип активности изменён');
      return;
    }

    // Режим добавления: создаём новые активности
    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    const currentEnd = timeEnd ? new Date(timeEnd) : sessionEnd;
    
    if (!currentStart || !currentEnd) {
      showError('Время сессии не инициализировано');
      return;
    }
    
    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const newActivities: ActivityItem[] = [];
    const timestamp = Date.now();

    let nextActivityStartTime: number;
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }

    // Как в EditSessionV2Modal: новые активности стартуют «в конец», затем всё
    // распределяется равномерно (в продвинутом — по timeStart, в упрощённом — по hours)
    const buildNewActivity = (id: string, typeId: number, contractId?: number, contractTitle?: string): ActivityItem => ({
      id,
      typeOfWorkId: typeId,
      typeOfWorkName: typesOfWork.find(t => t.id === typeId)?.name,
      ...(contractId != null ? { contractId, contractTitle } : {}),
      timeStart: new Date(nextActivityStartTime),
      rate: hourlyDefaultRate,
      hours: 0,
      percentage: 0,
      amount: 0,
      isNew: true,
      isModified: false,
      comment: '',
      additionalWorkReason: undefined,
      isAdditionalWork: false
    });

    if (isDealMode) {
      const selectedContracts = contracts.filter(c => selectedContractIds.includes(c.id));
      selectedContracts.forEach((contract, index) => {
        newActivities.push(buildNewActivity(`new_${timestamp}_${index}`, contract.typeOfWorkId, contract.id, contract.title));
      });
    } else {
      selectedWorkTypeIds.forEach((typeId, index) => {
        newActivities.push(buildNewActivity(`new_${timestamp}_${index}`, typeId));
      });
    }

    const allActivities = [...sorted, ...newActivities];
    if (isAdvancedMode) {
      setActivities(recalculateStats(distributeActivitiesEvenly(allActivities, currentStart, currentEnd)));
    } else {
      const totalHours = (currentEnd.getTime() - currentStart.getTime()) / 3600000;
      const hoursEach = allActivities.length > 0 ? totalHours / allActivities.length : 0;
      const distributed = allActivities.map(a => ({ ...a, hours: hoursEach, isModified: true }));
      setActivities(recalculateStats(distributed));
    }
    
    setShowWorkTypeSelector(false);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    
    showSuccess(`Добавлено активностей: ${newActivities.length}`);
  };

  /**
   * Закрывает модальное окно выбора активностей
   */
  const handleCloseWorkTypeSelector = () => {
    setShowWorkTypeSelector(false);
    setEditingActivityId(null);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
  };

  const handleQuickPickWorkType = useCallback((typeId: number) => {
    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    const currentEnd = timeEnd ? new Date(timeEnd) : sessionEnd;
    if (!currentStart || !currentEnd) {
      showError('Время сессии не инициализировано');
      return;
    }

    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const timestamp = Date.now();

    let nextActivityStartTime: number;
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }

    const newActivity: ActivityItem = {
      id: `quick_${timestamp}_${typeId}`,
      typeOfWorkId: typeId,
      typeOfWorkName: typesOfWork.find((t) => t.id === typeId)?.name,
      timeStart: new Date(nextActivityStartTime),
      rate: hourlyDefaultRate,
      hours: 0,
      percentage: 0,
      amount: 0,
      isNew: true,
      isModified: false,
      comment: '',
      additionalWorkReason: undefined,
      isAdditionalWork: false
    };

    const merged = [...sorted, newActivity];
    let result: ActivityItem[];
    if (isAdvancedMode) {
      result = distributeActivitiesEvenly(merged, currentStart, currentEnd);
    } else {
      const totalHours = (currentEnd.getTime() - currentStart.getTime()) / 3600000;
      const hoursEach = merged.length > 0 ? totalHours / merged.length : 0;
      result = merged.map(a => ({ ...a, hours: hoursEach, isModified: true }));
    }

    setActivities(recalculateStats(result));
    const selectedTypeName = typesOfWork.find((t) => t.id === typeId)?.name ?? 'Активность';
    showSuccess(`Добавлена активность: ${selectedTypeName}`);
  }, [
    activities,
    timeStart,
    sessionStart,
    timeEnd,
    sessionEnd,
    isAdvancedMode,
    getActivityEndTime,
    distributeActivitiesEvenly,
    recalculateStats,
    typesOfWork,
    hourlyDefaultRate,
    showError,
    showSuccess
  ]);

  /**
   * Удаление активности
   */
  const handleDeleteActivity = (id: number | string) => {
    const updated = activities.filter(a => a.id !== id);
    const final = recalculateStats(updated);
    setActivities(final);
    showSuccess('Активность удалена');
  };

  /**
   * Обработчик клика на кнопку комментария
   */
  const handleCommentClick = (item: ActivityItem) => {
    setCurrentActivityForComment(item);
    setActivityComment(item.comment || '');
    setCommentPhotos(item.activityCommentPhotos ?? []);
    setCommentPhotoPreviews([]);
    setShowCommentSheet(true);
  };

  useEffect(() => {
    if (!showCommentSheet || commentPhotos.length === 0) {
      commentPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      commentPhotoUrlsRef.current.clear();
      setCommentPhotoPreviews([]);
      return;
    }
    const urls = commentPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => commentPhotoUrlsRef.current.add(u));
    setCommentPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        commentPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showCommentSheet, commentPhotos]);

  const handleCommentPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && isImageFile(f));
    const max = 10;
    const toAdd = valid.slice(0, max - commentPhotos.length);
    setCommentPhotos((prev) => [...prev, ...toAdd]);
    if (commentFileInputRef.current) commentFileInputRef.current.value = '';
  };

  const handleRemoveCommentPhoto = (index: number) => {
    setCommentPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Обработчик клика на кнопку «доп.работа». При создании смены флаг сохраняется при нажатии «Создать смену».
   * Для уже помеченного допа подставляем текущий текст пометки, чтобы можно было редактировать.
   */
  const handleAdditionalWorkClick = (item: ActivityItem) => {
    setCurrentActivityForAdditionalWork(item);
    setAdditionalWorkReason(item.isAdditionalWork ? (item.additionalWorkReason || '') : '');
    setAdditionalWorkPhotos(item.additionalWorkPhotos ?? []);
    setShowAdditionalWorkSheet(true);
  };

  // Превью для фото доп.работы
  useEffect(() => {
    if (!showAdditionalWorkSheet || additionalWorkPhotos.length === 0) {
      additionalWorkPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      additionalWorkPhotoUrlsRef.current.clear();
      setAdditionalWorkPhotoPreviews([]);
      return;
    }
    const urls = additionalWorkPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => additionalWorkPhotoUrlsRef.current.add(u));
    setAdditionalWorkPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        additionalWorkPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showAdditionalWorkSheet, additionalWorkPhotos]);

  const isImageFile = (file: File): boolean =>
    (file.type && file.type.startsWith('image/')) ||
    ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'].some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

  const handleAdditionalWorkPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && isImageFile(f));
    const max = 10;
    const toAdd = valid.slice(0, max - additionalWorkPhotos.length);
    setAdditionalWorkPhotos((prev) => [...prev, ...toAdd]);
    if (additionalWorkFileInputRef.current) additionalWorkFileInputRef.current.value = '';
  };

  const handleRemoveAdditionalWorkPhoto = (index: number) => {
    setAdditionalWorkPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Переключить флаг «доп.работа» для активности (только локальное состояние при создании смены).
   * При помечении как доп — сохраняем причину и фото в активность; при снятии — тоже сохраняем последнее редактирование.
   */
  const handleToggleAdditionalWork = () => {
    if (!currentActivityForAdditionalWork) return;
    const item = currentActivityForAdditionalWork;
    updateActivity(item.id, {
      additionalWorkReason: additionalWorkReason || undefined,
      additionalWorkPhotos: item.isAdditionalWork ? undefined : (additionalWorkPhotos.length > 0 ? additionalWorkPhotos : undefined),
    });
    setActivities(prev => prev.map(a =>
      a.id === item.id
        ? { ...a, isAdditionalWork: !item.isAdditionalWork, additionalWorkPhotos: item.isAdditionalWork ? undefined : (additionalWorkPhotos.length > 0 ? additionalWorkPhotos : undefined) }
        : a
    ));
    showSuccess(item.isAdditionalWork ? 'Пометка "доп.работа" снята' : 'Активность помечена как доп.работа (сохранится при создании смены)');
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  };

  /**
   * Обновляет активность
   */
  const updateActivity = useCallback((id: number | string, updates: Partial<ActivityItem>) => {
    setActivities(prev => {
      const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const currentIndex = sorted.findIndex(item => item.id === id);
      
      if (currentIndex === -1) {
        return prev;
      }
      
      const updated = [...sorted];
      
      if (updates.timeStart) {
        const { start: currentStart, end: currentEnd } = sessionTimeRange;
        const newStart = updates.timeStart.getTime();
        
        const minStart = currentIndex > 0 
          ? sorted[currentIndex - 1].timeStart.getTime() + 60000
          : currentStart.getTime();
        
        const maxStart = currentIndex < sorted.length - 1
          ? sorted[currentIndex + 1].timeStart.getTime() - 60000
          : currentEnd.getTime() - 60000;
        
        const clampedStart = Math.max(minStart, Math.min(newStart, maxStart));
        
        updated[currentIndex] = {
          ...updated[currentIndex],
          timeStart: new Date(clampedStart),
          isModified: true
        };
      }
      
      if (updates.hours !== undefined) {
        updated[currentIndex] = {
          ...updated[currentIndex],
          hours: Math.max(0, updates.hours),
          isModified: true
        };
      }
      
      if (
        updates.typeOfWorkId !== undefined ||
        updates.contractId !== undefined ||
        updates.rate !== undefined ||
        updates.comment !== undefined ||
        updates.additionalWorkReason !== undefined ||
        updates.additionalWorkPhotos !== undefined ||
        updates.activityCommentPhotos !== undefined
      ) {
        const merged = {
          ...updated[currentIndex],
          ...updates,
          isModified: true
        };
        if (updates.typeOfWorkId !== undefined) {
          const workType = typesOfWork.find(t => t.id === updates.typeOfWorkId);
          merged.typeOfWorkName = workType?.name;
        }
        updated[currentIndex] = merged;
      }

      return recalculateStats(updated);
    });
  }, [sessionTimeRange, recalculateStats, typesOfWork]);

  const handleInlineQuickPickForActivity = useCallback((item: ActivityItem, typeId: number) => {
    updateActivity(item.id, {
      typeOfWorkId: typeId,
      contractId: undefined,
      contractTitle: undefined
    });
    setInlinePickerActivityId(null);
    const selectedTypeName = typesOfWork.find((t) => t.id === typeId)?.name ?? 'Активность';
    showSuccess(`Выбрана активность: ${selectedTypeName}`);
  }, [updateActivity, typesOfWork, showSuccess]);

  /**
   * Сохранить текст пометки в активность и закрыть окно доп.работы (при создании смены).
   */
  const handleCloseAdditionalWorkSheet = useCallback(() => {
    if (currentActivityForAdditionalWork?.isAdditionalWork) {
      updateActivity(currentActivityForAdditionalWork.id, {
        additionalWorkReason: additionalWorkReason || undefined,
        additionalWorkPhotos: additionalWorkPhotos.length > 0 ? additionalWorkPhotos : undefined,
      });
    }
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  }, [currentActivityForAdditionalWork, additionalWorkReason, additionalWorkPhotos, updateActivity]);

  // Мемоизация функций
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDuration = useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return { hours: h, minutes: m };
  }, []);

  // Оптимизация: кешируем отсортированные активности
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
  }, [activities]);

  // Получаем времена пересечений (между активностями)
  const intersectionTimes = useMemo((): Date[] => {
    if (sortedActivities.length < 2) return [];
    return sortedActivities.slice(1).map((activity, arrayIndex) => {
      const handleIndex = arrayIndex;
      if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
        return tempTimelinePositions.get(handleIndex)!;
      }
      return activity.timeStart;
    });
  }, [sortedActivities, tempTimelinePositions]);

  // Получаем информацию о сегментах временной шкалы
  const timelineSegments = useMemo((): Array<{ start: Date; end: Date; activity: ActivityItem; duration?: number }> => {
    if (sortedActivities.length === 0) return [];
    
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    
    return sortedActivities.map((activity, index) => {
      let start: Date;
      let end: Date;
      
      // Начало сегмента
      if (index === 0) {
        start = currentStart;
      } else {
        const handleIndex = index - 1;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          start = tempTimelinePositions.get(handleIndex)!;
        } else {
          start = activity.timeStart;
        }
      }
      
      // Конец сегмента
      if (index < sortedActivities.length - 1) {
        const handleIndex = index;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          end = tempTimelinePositions.get(handleIndex)!;
        } else {
          end = sortedActivities[index + 1].timeStart;
        }
      } else {
        end = currentEnd;
      }
      
      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return { start, end, activity, duration };
    });
  }, [sortedActivities, sessionTimeRange, tempTimelinePositions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedExecutorId) {
      showError('Выберите исполнителя');
      return;
    }

    if (!timeStart) {
      showError('Укажите время начала смены');
      return;
    }

    if (!timeEnd) {
      showError('Укажите время окончания смены');
      return;
    }

    if (!user) {
      showError('Пользователь не авторизован');
      return;
    }

    // Проверяем, что все активности имеют вид работы или контракт
    const activitiesWithoutSource = activities.filter(a =>
      (!a.typeOfWorkId || a.typeOfWorkId === 0) && !a.contractId
    );
    if (activitiesWithoutSource.length > 0) {
      showError('Укажите вид работы или контракт для всех активностей');
      return;
    }

    // Если активностей нет, требуем выбрать хотя бы одну
    if (activities.length === 0) {
      showError('Добавьте хотя бы одну активность');
      return;
    }

    try {
      setLoading(true);

      // Подготавливаем активности для отправки
      const newStart = new Date(timeStart);
      const newEnd = new Date(timeEnd);
      const sortedActivities = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      
      const activitiesData: CreateSessionActivityRequest[] = sortedActivities.map((item, index) => {
        if (isAdvancedMode) {
          const endTime = getActivityEndTime(sortedActivities, index, newEnd);
          if (endTime.getTime() <= item.timeStart.getTime()) {
            throw new Error(`Некорректное время для активности #${index + 1}`);
          }
          return {
            typeOfWorkId: item.typeOfWorkId,
            timeStart: item.timeStart.toISOString(),
            timeEnd: endTime.toISOString(),
            rate: Number(item.rate) || 0,
            contractId: item.contractId,
            comment: item.comment || '',
            isAdditionalWork: item.isAdditionalWork ?? false,
            additionalWorkReason: item.isAdditionalWork ? (item.additionalWorkReason || undefined) : undefined
          };
        }
        return {
          typeOfWorkId: item.typeOfWorkId,
          timeStart: undefined,
          timeEnd: undefined,
          allocatedHours: item.hours,
          sortOrder: index,
          rate: Number(item.rate) || 0,
          contractId: item.contractId,
          comment: item.comment || '',
          isAdditionalWork: item.isAdditionalWork ?? false,
          additionalWorkReason: item.isAdditionalWork ? (item.additionalWorkReason || undefined) : undefined
        };
      });

      // Используем тип работы первой активности для смены (для обратной совместимости)
      // Но если передаем activities, typeOfWorks не обязателен
      const firstActivity = activities[0];
      
      // Преобразуем локальное время в UTC для отправки на сервер
      const localStart = new Date(timeStart);
      const localEnd = new Date(timeEnd);
      
      // Проверяем, что даты валидны
      if (isNaN(localStart.getTime()) || isNaN(localEnd.getTime())) {
        showError('Некорректное время начала или окончания смены');
        setLoading(false);
        return;
      }
      
      // Преобразуем в UTC ISO строку
      // toISOString() автоматически конвертирует локальное время в UTC
      const utcStart = localStart.toISOString();
      const utcEnd = localEnd.toISOString();
      
      const sessionData: CreateSessionV2Request = {
        siteExecutorId: selectedExecutorId ?? undefined,
        typeOfWorks: activitiesData.length > 0 ? firstActivity.typeOfWorkId : undefined,
        timeStart: utcStart,
        timeEnd: utcEnd,
        contractId: undefined,
        activities: activitiesData // Передаем активности в одном запросе
      };

      console.log('Создание смены:', {
        siteId,
        siteExecutorId: selectedExecutorId,
        timeStart: {
          local: timeStart,
          localDate: localStart.toISOString(),
          utc: utcStart
        },
        timeEnd: {
          local: timeEnd,
          localDate: localEnd.toISOString(),
          utc: utcEnd
        },
        activitiesCount: activitiesData.length
      });

      // Создаем смену вместе с активностями в одном запросе
      const result = await sessionsV2Api.createSession(siteId, sessionData);

      const activityIds = result.activityIds ?? [];
      const opts = { maxWidth: 2560, maxHeight: 2560, quality: 0.85, maxSizeMB: 5, outputFormat: 'image/webp' as const };

      // Загрузка фото доп.работы
      const additionalWorkToUpload = sortedActivities
        .map((a, i) => ({ activityId: activityIds[i], files: a.additionalWorkPhotos }))
        .filter((x): x is { activityId: number; files: File[] } => typeof x.activityId === 'number' && (x.files?.length ?? 0) > 0);
      if (additionalWorkToUpload.length > 0) {
        try {
          await Promise.all(
            additionalWorkToUpload.map(async ({ activityId, files }) => {
              const compressed = await compressImageFiles(files, opts);
              await sessionsV2Api.uploadAdditionalWorkPhotos(siteId, result.id, activityId, compressed);
            })
          );
        } catch (photoErr: unknown) {
          console.error('Ошибка загрузки фото доп.работы:', photoErr);
          showError('Смена создана, но не все фото доп.работы загружены');
        }
      }

      // Загрузка фото комментариев к активностям
      const commentPhotosToUpload = sortedActivities
        .map((a, i) => ({ activityId: activityIds[i], files: a.activityCommentPhotos }))
        .filter((x): x is { activityId: number; files: File[] } => typeof x.activityId === 'number' && (x.files?.length ?? 0) > 0);
      if (commentPhotosToUpload.length > 0) {
        try {
          await Promise.all(
            commentPhotosToUpload.map(async ({ activityId, files }) => {
              const compressed = await compressImageFiles(files, opts);
              await sessionsV2Api.uploadActivityCommentPhotos(siteId, result.id, activityId, compressed);
            })
          );
        } catch (photoErr: unknown) {
          console.error('Ошибка загрузки фото комментариев:', photoErr);
          showError('Смена создана, но не все фото комментариев загружены');
        }
      }

      showSuccess('Смена создана');
      
      // Сброс формы
      setSelectedTypeOfWorksId(null);
      setSelectedExecutorId(null);
      setTimeStart('');
      setTimeEnd('');
      setActivities([]);
      setTimeSuggestions(null);
      
      onSuccess();
    } catch (err: any) {
      console.error('Error creating session:', err);
      
      // Извлекаем сообщение об ошибке из ответа сервера
      let errorMessage = 'Ошибка создания смены';
      
      if (err?.response) {
        const responseData = err.response.data;
        
        if (err.response.status === 403) {
          // Для 403 ошибок
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (responseData?.error) {
            errorMessage = responseData.error;
          } else {
            errorMessage = 'У вас нет прав на создание смен';
          }
        } else if (err.response.status === 400) {
          // Для 400 ошибок (включая пересечение смен)
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (responseData?.error) {
            errorMessage = responseData.error;
          } else if (responseData?.message) {
            errorMessage = responseData.message;
          } else if (responseData) {
            errorMessage = String(responseData);
          }
        } else {
          // Для других статусов
          if (typeof responseData === 'string') {
            errorMessage = responseData;
          } else if (responseData?.error) {
            errorMessage = responseData.error;
          } else if (responseData?.message) {
            errorMessage = responseData.message;
          }
        }
      } else if (err?.message) {
        // Если ошибка без response, используем message
        errorMessage = err.message;
      }

      if (errorMessage.includes('overlaps') || errorMessage.includes('пересекается') || errorMessage.includes('overlap')) {
        errorMessage = 'Время активности пересекается с существующей активностью. Проверьте временные интервалы.';
      }
      
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTypeOfWorksId(null);
    setSelectedExecutorId(null);
    setTimeStart('');
    setTimeEnd('');
    setActivities([]);
    setIsAdvancedMode(false);
    setTimeSuggestions(null);
    setShowWorkTypeSelector(false);
    setSelectedWorkTypeIds([]);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Создать смену"
      size="large"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
    >
      <form onSubmit={handleSubmit} className="create-session-form">
        <div className="create-session-form-content">
          <ExecutorSelector
            value={selectedExecutorId}
            onChange={setSelectedExecutorId}
            siteId={siteId}
            placeholder="Выберите исполнителя"
            required
          />

          <div className="form-group-compact time-range-group">
            <div className="time-range-inputs">
              <div className="time-range-item">
                <label className="time-range-label">Начало *</label>
                <input
                  type="datetime-local"
                  id="timeStart"
                  value={timeStart}
                  onChange={(e) => setTimeStart(e.target.value)}
                  required
                />
                {timeSuggestions && timeSuggestions.primaryStartTime && (
                  <div className="time-suggestions">
                    {[
                      timeSuggestions.primaryStartTime,
                      ...timeSuggestions.alternativeStartTimes
                    ].map((time, idx) => {
                      const today = new Date();
                      const year = today.getFullYear();
                      const month = String(today.getMonth() + 1).padStart(2, '0');
                      const day = String(today.getDate()).padStart(2, '0');
                      const [hours, minutes] = time.split(':');
                      const dateTimeString = `${year}-${month}-${day}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className="time-suggestion-btn"
                          onClick={() => {
                            setTimeStart(dateTimeString);
                            const suggestedStart = new Date(today);
                            suggestedStart.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                            setSessionStart(suggestedStart);
                          }}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="time-range-separator">
                <i className="ri-arrow-right-line"></i>
              </div>
              <div className="time-range-item">
                <label className="time-range-label">Конец *</label>
                <input
                  type="datetime-local"
                  id="timeEnd"
                  value={timeEnd}
                  onChange={(e) => setTimeEnd(e.target.value)}
                  required
                />
                {timeSuggestions && timeSuggestions.primaryEndTime && (
                  <div className="time-suggestions">
                    {[
                      timeSuggestions.primaryEndTime,
                      ...timeSuggestions.alternativeEndTimes
                    ].map((time, idx) => {
                      const today = new Date();
                      const year = today.getFullYear();
                      const month = String(today.getMonth() + 1).padStart(2, '0');
                      const day = String(today.getDate()).padStart(2, '0');
                      const [hours, minutes] = time.split(':');
                      const dateTimeString = `${year}-${month}-${day}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className="time-suggestion-btn"
                          onClick={() => {
                            setTimeEnd(dateTimeString);
                            const suggestedEnd = new Date(today);
                            suggestedEnd.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                            setSessionEnd(suggestedEnd);
                          }}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Секция активностей */}
        <div className="activities-section-wrapper">
          <div className="activities-section-header">
            <div className="activities-section-title-container">
              <div className="section-title">
                <h3>Активности</h3>
              </div>
            </div>
            <div className="activities-section-divider"></div>
          </div>
          <div className="activities-section-actions">
            <button
              type="button"
              className="btn-add-row"
              onClick={() => {
                setIsAdvancedMode(prev => {
                  const next = !prev;
                  if (next) {
                    setActivities(a => {
                      const start = timeStart ? new Date(timeStart) : sessionStart;
                      const end = timeEnd ? new Date(timeEnd) : sessionEnd;
                      const converted = hoursToTimePoints(a, start, end);
                      return recalculateStats(converted, true);
                    });
                  } else {
                    setActivities(a => recalculateStats(a, false));
                  }
                  return next;
                });
              }}
              title={isAdvancedMode ? 'Переключить на упрощённый режим (только продолжительность)' : 'Переключить на продвинутый режим (точное время начала и конца)'}
              style={{ padding: '8px 12px', fontSize: '13px' }}
            >
              <i className={isAdvancedMode ? 'ri-time-line' : 'ri-hourglass-line'}></i>
              <span>{isAdvancedMode ? 'Упрощённый режим' : 'Продвинутый режим'}</span>
            </button>
          </div>
          <div className="activity-quick-add-wrap">
            <button
              type="button"
              className={`btn-add-row activity-quick-add-toggle${showQuickAddActivityBar ? ' activity-quick-add-toggle--open' : ''}`}
              onClick={() => setShowQuickAddActivityBar((v) => !v)}
              aria-expanded={showQuickAddActivityBar}
              title={showQuickAddActivityBar ? 'Скрыть выбор активностей' : 'Показать быстрый выбор активностей'}
            >
              <i className="ri-add-line"></i>
              <span>Добавить активность</span>
              <i className="ri-arrow-down-s-line activity-quick-add-chevron" aria-hidden />
            </button>
            {showQuickAddActivityBar && (
              <div className="activity-quick-picker-panel" role="region" aria-label="Быстрый выбор активностей">
                <div className="activity-quick-picker-row">
                  <button
                    type="button"
                    className="activity-quick-picker-btn activity-quick-picker-btn-global"
                    onClick={handleAddActivity}
                    title="Открыть полный выбор активностей"
                  >
                    <i className="ri-list-check-2"></i>
                  </button>
                  {quickWorkTypeTabs.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className="activity-quick-picker-btn"
                      onClick={() => handleQuickPickWorkType(type.id)}
                      title={`Добавить активность: ${type.name}`}
                    >
                      {type.name}
                    </button>
                  ))}
                  {loadingRecentActivities && (
                    <span className="activity-quick-picker-loading" aria-live="polite">
                      <i className="ri-loader-4-line ri-spin"></i>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="activities-section">
            {/* Временная шкала с ползунками */}
            {activities.length >= 1 && (() => {
              // Преобразуем сегменты в формат Timeline
              const timelineSegmentsData: TimelineSegment[] = timelineSegments.map((segment) => {
                const workTypeName = segment.activity.contractTitle ||
                  segment.activity.typeOfWorkName ||
                  typesOfWork.find(t => t.id === segment.activity.typeOfWorkId)?.name ||
                  'Не выбрано';
                
                return {
                  id: segment.activity.id,
                  start: segment.start,
                  end: segment.end,
                  label: workTypeName,
                  duration: segment.duration !== undefined 
                    ? segment.duration 
                    : segment.activity.hours,
                  data: segment.activity
                };
              });

              // Преобразуем точки перетаскивания в формат Timeline
              const timelineHandles: TimelineHandle[] = intersectionTimes.map((time, index) => ({ id: `handle-${index}`, time }));

              // Форматирование длительности
              const formatDurationDisplay = (hours: number): string => {
                const h = Math.floor(hours);
                const m = Math.round((hours - h) * 60);
                if (m === 0) {
                  return `${h}ч`;
                }
                return `${h}ч ${m}м`;
              };

              return (
                <Timeline
                  key={isAdvancedMode ? 'advanced' : 'simple'}
                  startTime={sessionStart}
                  endTime={sessionEnd}
                  segments={timelineSegmentsData}
                  handles={timelineHandles}
                  onHandleDrag={(handleId, newTime) => {
                    const handleIndex = parseInt(handleId.toString().replace('handle-', ''));
                    if (isNaN(handleIndex) || sortedActivities.length <= handleIndex + 1) return;
                    if (isAdvancedMode) {
                      const nextActivity = sortedActivities[handleIndex + 1];
                      setActivities(prev => {
                        const index = prev.findIndex(a => a.id === nextActivity.id);
                        if (index === -1) return prev;
                        const updated = [...prev];
                        updated[index] = { ...updated[index], timeStart: newTime, isModified: true };
                        return recalculateStats(updated);
                      });
                    } else {
                      // Упрощённый режим: меняем только часы двух соседних активностей (как в продвинутом — двигается только перетаскиваемый ползунок)
                      const { start: sessStart, end: sessEnd } = sessionTimeRange;
                      if (!sessStart || !sessEnd) return;
                      const MS_PER_HOUR = 3600000;
                      const MIN_HOURS = 0.25;
                      setActivities(prev => {
                        const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
                        let cumulative = sessStart.getTime();
                        for (let i = 0; i < handleIndex; i++) {
                          cumulative += (sorted[i].hours || 0) * MS_PER_HOUR;
                        }
                        const oldHoursN = sorted[handleIndex].hours || 0;
                        const newHoursN = Math.max(MIN_HOURS, (newTime.getTime() - cumulative) / MS_PER_HOUR);
                        const delta = newHoursN - oldHoursN;
                        const oldHoursN1 = sorted[handleIndex + 1]?.hours || 0;
                        const newHoursN1 = Math.max(MIN_HOURS, oldHoursN1 - delta);
                        const newHours = sorted.map((item, idx) => {
                          if (idx === handleIndex) return newHoursN;
                          if (idx === handleIndex + 1) return newHoursN1;
                          return item.hours || 0;
                        });
                        const withHours = sorted.map((item, idx) => ({ ...item, hours: newHours[idx], isModified: true }));
                        return recalculateStats(withHours);
                      });
                    }
                  }}
                  onHandleDragEnd={() => {}}
                  formatTime={formatTime}
                  formatDuration={formatDurationDisplay}
                  minSegmentWidth={8}
                  showTimeLabels={isAdvancedMode}
                  showDuration={true}
                  showLabels={true}
                  timelineRef={setTimelineRef}
                  className="activities-timeline"
                />
              );
            })()}

            <div className="activities-table">
              <div className="table-body">
                {activities.length > 0 && (
                  <>
                    {/* Разделитель и время начала первой активности */}
                    <div className="activity-separator">
                      <div className="activity-separator-line"></div>
                      {isAdvancedMode && (
                        <div className="activity-separator-time">
                          <span title="Начало смены" className="activity-start-time-label">
                            {formatTime(activities[0].timeStart)}
                          </span>
                        </div>
                      )}
                    </div>
                    {activities.map((item, index) => (
                      <ActivityItemComponent
                        key={item.id}
                        item={item}
                        index={index}
                        activities={activities}
                        sessionEnd={sessionEnd}
                        formatTime={formatTime}
                        formatDuration={formatDuration}
                        getActivityEndTime={getActivityEndTime}
                        updateActivity={updateActivity}
                        handleDeleteActivity={handleDeleteActivity}
                        onCommentClick={handleCommentClick}
                        onAdditionalWorkClick={handleAdditionalWorkClick}
                        onActivityTypeClick={handleToggleActivityInlinePicker}
                        onActivityTypeGlobalPick={handleOpenActivityTypeEditor}
                        onCloseInlinePicker={handleCloseInlinePicker}
                        onInlineQuickPick={handleInlineQuickPickForActivity}
                        quickWorkTypeTabs={quickWorkTypeTabs}
                        inlinePickerActivityId={inlinePickerActivityId}
                        isLast={index === activities.length - 1}
                        isSimpleMode={!isAdvancedMode}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="create-session-form-content">
          <div className="form-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading || activities.length === 0}
              icon="ri-calendar-line"
            >
              Создать смену
            </StatusActionButton>
          </div>
        </div>
      </form>

      {/* Модальное окно выбора активностей: Ставка (виды работ) | Сделка (контракты) */}
      <BottomSheet
        isOpen={showWorkTypeSelector}
        onClose={handleCloseWorkTypeSelector}
        title={editingActivityId !== null ? 'Изменить тип активности' : 'Выберите активности'}
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
          {/* Вкладки Ставка | Сделка — на всю ширину, выделены */}
          <div className="activity-source-tabs-wrapper">
            <HorizontalCategoryTabs
              categories={['Ставка', 'Сделка']}
              value={activitySourceTab === 'ставка' ? 'Ставка' : 'Сделка'}
              onChange={(v) => setActivitySourceTab(v === 'Сделка' ? 'сделка' : 'ставка')}
              showAllOption={false}
              size="medium"
              className="activity-source-tabs"
            />
          </div>

          {activitySourceTab === 'ставка' && (
            <>
              {/* Поиск */}
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    ref={workTypeSearchInputRef}
                    type="text"
                    placeholder="Поиск по названию или категории..."
                    value={workTypeSearchQuery}
                    onChange={(e) => setWorkTypeSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Фильтр по категориям */}
              {(() => {
                const categories = Array.from(new Set(typesOfWork.map(type => type.section)));
                return categories.length > 0 && (
                  <HorizontalCategoryTabs
                    categories={categories}
                    value={selectedWorkTypeCategory}
                    onChange={setSelectedWorkTypeCategory}
                    showAllOption={true}
                    allOptionLabel="Все категории"
                    size="medium"
                  />
                );
              })()}

              {/* Список типов работ */}
              <div className="work-type-list">
                {loadingWorkTypes ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка типов работ...</span>
                  </div>
                ) : (() => {
                  const filteredTypes = typesOfWork.filter(type => {
                    const matchesSearch = type.name.toLowerCase().includes(workTypeSearchQuery.toLowerCase()) ||
                                         type.section.toLowerCase().includes(workTypeSearchQuery.toLowerCase());
                    const matchesCategory = !selectedWorkTypeCategory || type.section === selectedWorkTypeCategory;
                    return matchesSearch && matchesCategory;
                  });

                  const groupedTypes = filteredTypes.reduce((acc, type) => {
                    if (!acc[type.section]) {
                      acc[type.section] = [];
                    }
                    acc[type.section].push(type);
                    return acc;
                  }, {} as Record<string, TypeOfWorkV2[]>);

                  return Object.keys(groupedTypes).length === 0 ? (
                    <div className="work-type-empty">
                      <i className="ri-search-line"></i>
                      <span>Типы работ не найдены</span>
                    </div>
                  ) : (
                    Object.entries(groupedTypes).map(([category, types]) => (
                      <div key={category} className="work-type-category-group">
                        <div className="category-header">
                          <h4>{category}</h4>
                          <span className="category-count">{types.length}</span>
                        </div>
                        <div className="work-type-grid">
                          {types.map(type => {
                            const isSelected = selectedWorkTypeIds.includes(type.id);
                            return (
                              <div
                                key={type.id}
                                className={`work-type-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => toggleWorkTypeSelection(type.id)}
                              >
                                <div className="work-type-card-content">
                                  <div className="work-type-card-name">{type.name}</div>
                                </div>
                                {isSelected && (
                                  <div className="work-type-card-check">
                                    <i className="ri-check-line"></i>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  );
                })()}
              </div>
            </>
          )}

          {activitySourceTab === 'сделка' && (
            <>
              {/* Поиск по контрактам */}
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    ref={contractSearchInputRef}
                    type="text"
                    placeholder="Поиск по названию контракта..."
                    value={contractSearchQuery}
                    onChange={(e) => setContractSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>

              {/* Список контрактов */}
              <div className="work-type-list">
                {loadingContracts ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка контрактов...</span>
                  </div>
                ) : (() => {
                  const filteredContracts = contracts.filter(c =>
                    c.title.toLowerCase().includes(contractSearchQuery.toLowerCase()) ||
                    (c.description || '').toLowerCase().includes(contractSearchQuery.toLowerCase())
                  );

                  return filteredContracts.length === 0 ? (
                    <div className="work-type-empty">
                      <i className="ri-file-list-3-line"></i>
                      <span>Контракты не найдены</span>
                      <small>Создайте контракт в разделе «Контракты» объекта</small>
                    </div>
                  ) : (
                    <div className="work-type-grid contract-grid">
                      {filteredContracts.map(contract => {
                        const isSelected = selectedContractIds.includes(contract.id);
                        return (
                          <div
                            key={contract.id}
                            className={`work-type-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleContractSelection(contract.id)}
                          >
                            <div className="work-type-card-content">
                              <div className="work-type-card-name">{contract.title}</div>
                              {contract.description && (
                                <div className="work-type-card-subtitle">{contract.description}</div>
                              )}
                              <div className="work-type-card-subtitle">
                                {contract.fixedAmount.toLocaleString('ru-RU')} ₽
                              </div>
                            </div>
                            {isSelected && (
                              <div className="work-type-card-check">
                                <i className="ri-check-line"></i>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {/* Кнопки действий */}
          <div className="work-type-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleCloseWorkTypeSelector}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleConfirmWorkTypeSelection}
              disabled={(activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length) === 0}
            >
              {editingActivityId !== null
                ? 'Применить'
                : `Добавить (${activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length})`}
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для ввода комментария к активности */}
      <BottomSheet
        isOpen={showCommentSheet}
        onClose={() => {
          if (currentActivityForComment) {
            updateActivity(currentActivityForComment.id, {
              comment: activityComment ?? '',
              activityCommentPhotos: commentPhotos.length > 0 ? commentPhotos : undefined,
            });
          }
          setShowCommentSheet(false);
          setCurrentActivityForComment(null);
          setActivityComment('');
          setCommentPhotos([]);
          setCommentPhotoPreviews([]);
        }}
        title="Комментарий к активности"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Комментарий</label>
            <textarea
              ref={activityCommentTextareaRef}
              value={activityComment}
              onChange={(e) => setActivityComment(e.target.value)}
              placeholder="Введите комментарий к активности..."
              rows={5}
              style={{ width: '100%', padding: '12px', backgroundColor: '#2a2a2a', color: '#e0e0e0', border: '1px solid #444', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', minHeight: '100px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
            <input ref={commentFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleCommentPhotoSelect} />
            {commentPhotoPreviews.length < 10 && (
              <button type="button" onClick={() => openFileInput(commentFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({commentPhotoPreviews.length}/10)
              </button>
            )}
            {commentPhotoPreviews.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {commentPhotoPreviews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                    <button type="button" onClick={() => handleRemoveCommentPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <StatusActionButton type="button" variant="secondary" onClick={() => { setShowCommentSheet(false); setCurrentActivityForComment(null); setActivityComment(''); setCommentPhotos([]); setCommentPhotoPreviews([]); }} fullWidth>Отмена</StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={() => {
                if (currentActivityForComment) {
                  updateActivity(currentActivityForComment.id, {
                    comment: activityComment ?? '',
                    activityCommentPhotos: commentPhotos.length > 0 ? commentPhotos : undefined,
                  });
                  showSuccess('Комментарий сохранен (применится при создании смены)');
                }
                setShowCommentSheet(false);
                setCurrentActivityForComment(null);
                setActivityComment('');
                setCommentPhotos([]);
                setCommentPhotoPreviews([]);
              }}
              fullWidth
            >
              Сохранить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для флага «доп.работа». При создании смены доп ещё не в БД — при клике на горящий маркер показываем что написали и кнопку снять пометку (без причины снятия). */}
      <BottomSheet
        isOpen={showAdditionalWorkSheet}
        onClose={handleCloseAdditionalWorkSheet}
        title={currentActivityForAdditionalWork?.isAdditionalWork ? 'Доп.работа' : 'Пометить как доп.работу'}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Информация о текущем статусе */}
          <div style={{
            padding: '12px',
            backgroundColor: currentActivityForAdditionalWork?.isAdditionalWork ? 'rgba(255, 170, 0, 0.1)' : 'rgba(136, 136, 136, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i
              className={currentActivityForAdditionalWork?.isAdditionalWork ? "ri-star-fill" : "ri-star-line"}
              style={{
                fontSize: '24px',
                color: currentActivityForAdditionalWork?.isAdditionalWork ? '#ffaa00' : '#888'
              }}
            />
            <div>
              <div style={{ fontWeight: '500', color: '#e0e0e0' }}>
                {currentActivityForAdditionalWork?.isAdditionalWork ? 'Эта активность помечена как доп.работа' : 'Эта активность не помечена как доп.работа'}
              </div>
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {currentActivityForAdditionalWork?.typeOfWorkName || 'Вид работы не указан'}
              </div>
            </div>
          </div>

          {/* При клике на уже помеченный доп — показываем текст пометки (редактируемый) и кнопки Закрыть / Удалить пометку */}
          {currentActivityForAdditionalWork?.isAdditionalWork ? (
            <>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e0e0e0',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Причина допа (можно редактировать)
                </label>
                <textarea
                  ref={additionalWorkReasonTextareaRef}
                  value={additionalWorkReason}
                  onChange={(e) => setAdditionalWorkReason(e.target.value)}
                  placeholder="Опишите доп.работу..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#2a2a2a',
                    color: '#e0e0e0',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                />
              </div>
              {/* Фотографии */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
                <input ref={additionalWorkFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAdditionalWorkPhotoSelect} />
                {additionalWorkPhotoPreviews.length < 10 && (
                  <button type="button" onClick={() => openFileInput(additionalWorkFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                    <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({additionalWorkPhotoPreviews.length}/10)
                  </button>
                )}
                {additionalWorkPhotoPreviews.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {additionalWorkPhotoPreviews.map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                        <button type="button" onClick={() => handleRemoveAdditionalWorkPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <StatusActionButton type="button" variant="secondary" onClick={handleCloseAdditionalWorkSheet} fullWidth>Закрыть</StatusActionButton>
                <StatusActionButton type="button" variant="danger" onClick={handleToggleAdditionalWork} fullWidth>Удалить пометку доп</StatusActionButton>
              </div>
            </>
          ) : (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Причина (необязательно)</label>
                <textarea ref={additionalWorkReasonTextareaRef} value={additionalWorkReason} onChange={(e) => setAdditionalWorkReason(e.target.value)} placeholder="Почему это доп.работа..." rows={3} style={{ width: '100%', padding: '12px', backgroundColor: '#2a2a2a', color: '#e0e0e0', border: '1px solid #444', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', minHeight: '80px' }} />
              </div>
              {/* Фотографии */}
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
                <input ref={additionalWorkFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAdditionalWorkPhotoSelect} />
                {additionalWorkPhotoPreviews.length < 10 && (
                  <button type="button" onClick={() => openFileInput(additionalWorkFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                    <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({additionalWorkPhotoPreviews.length}/10)
                  </button>
                )}
                {additionalWorkPhotoPreviews.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {additionalWorkPhotoPreviews.map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                        <button type="button" onClick={() => handleRemoveAdditionalWorkPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                          <i className="ri-close-line" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <StatusActionButton type="button" variant="secondary" onClick={() => { setShowAdditionalWorkSheet(false); setCurrentActivityForAdditionalWork(null); setAdditionalWorkReason(''); setAdditionalWorkPhotos([]); setAdditionalWorkPhotoPreviews([]); }} fullWidth>Отмена</StatusActionButton>
                <StatusActionButton type="button" variant="primary" onClick={handleToggleAdditionalWork} fullWidth>Пометить как доп.работу</StatusActionButton>
              </div>
            </>
          )}
        </div>
      </BottomSheet>
    </BottomSheet>
  );
};

export default CreateSessionV2Modal;
